import React from 'react';

/**
 * NishaAvatar — animated illustrated support-agent for the "Ask Nisha" chatbot.
 *
 * Motion baked in:
 *   • Idle breathing (whole avatar gently scales up/down)
 *   • Blinking every ~5 s (eyes squash on the Y axis)
 *   • Hair sway (side hair tresses drift like a subtle breeze)
 *   • Blush glow (soft pulsing warmth on the cheeks)
 *   • `talking` prop → mouth opens/closes for the "typing…" indicator
 *   • Optional online-dot pulse
 *
 * Everything is pure SVG + CSS keyframes so there's no external dep, no
 * network hit, and it scales cleanly at any size.
 */
// Both AcreMitra founders — rotate every ~10s so both get face-time on the site.
const FOUNDERS = [
  { key: 'lisa',    src: '/nisha-founder.jpg', name: 'Lisa',    role: 'Co-founder \u00B7 Client Advisory', greeting: 'Hi, I\u2019m Lisa \u2014 ask me anything about plots, prices or paperwork.' },
  { key: 'sounav',  src: '/ram-founder.jpg',   name: 'Sounav',  role: 'Co-founder \u00B7 Growth & Ops',    greeting: 'Hi, I\u2019m Sounav \u2014 ask me about corridor ROI, projects, or on-site visits.' },
];

// Helper for callsites that need to read the current founder without mounting an avatar.
export const useCurrentFounder = () => {
  const [i, setI] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % FOUNDERS.length), 10000);
    return () => clearInterval(t);
  }, []);
  return FOUNDERS[i];
};

export { FOUNDERS };

export const NishaAvatar = ({
  size = 40,
  withStatus = false,
  talking = false,
  className = '',
  showBio = false,        // when true, show a hover / tap bio popover with name + role
  bioPlacement = 'top',   // 'top' | 'bottom' | 'left' | 'right'
  founderKey,             // 'nisha' | 'ram' — optional override to pin an avatar
}) => {
  // Scoped id so multiple instances on the page don't collide on gradients
  // or CSS animation names (blur, gradient IDs, etc.).
  const uid = React.useId().replace(/:/g, '');
  const [photoOk, setPhotoOk] = React.useState(true);
  const [popOpen, setPopOpen] = React.useState(false);

  // Rotate founders every 10s unless a specific one is pinned via prop
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    if (founderKey) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % FOUNDERS.length), 10000);
    return () => clearInterval(t);
  }, [founderKey]);

  const founder = founderKey
    ? (FOUNDERS.find((f) => f.key === founderKey) || FOUNDERS[0])
    : FOUNDERS[idx];

  const popStyle = {
    top:    { bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)' },
    bottom: { top: 'calc(100% + 8px)',    left: '50%', transform: 'translateX(-50%)' },
    left:   { right: 'calc(100% + 8px)',  top: '50%',  transform: 'translateY(-50%)' },
    right:  { left: 'calc(100% + 8px)',   top: '50%',  transform: 'translateY(-50%)' },
  }[bioPlacement] || {};

  return (
    <span
      className={`relative inline-flex items-center justify-center overflow-visible rounded-full shadow-sm ${className}`}
      style={{ width: size, height: size }}
      aria-label={`Ask ${founder.name}, ${founder.role}`}
      data-testid="nisha-avatar"
      onMouseEnter={() => showBio && setPopOpen(true)}
      onMouseLeave={() => showBio && setPopOpen(false)}
      onFocus={() => showBio && setPopOpen(true)}
      onBlur={() => showBio && setPopOpen(false)}
      tabIndex={showBio ? 0 : undefined}
    >
      {/* Avatar disc */}
      <span
        className="relative inline-flex items-center justify-center overflow-hidden rounded-full ring-2 ring-white/70"
        style={{ width: '100%', height: '100%' }}
      >
        {photoOk ? (
          <img
            key={founder.key}
            src={founder.src}
            alt={`${founder.name}, ${founder.role}`}
            className="w-full h-full object-cover"
            onError={() => setPhotoOk(false)}
            loading="lazy"
            style={{
              transformOrigin: '50% 60%',
              animation: `nisha-photo-swap-${uid} 0.55s ease-out, nisha-photo-breath-${uid} 4.4s ease-in-out infinite${talking ? `, nisha-photo-talk-${uid} 0.55s ease-in-out infinite` : ''}`,
            }}
            data-testid={`nisha-avatar-${founder.key}`}
          />
        ) : (
          <NishaSvgFallback uid={uid} talking={talking} />
        )}
        <style>{`
          @keyframes nisha-photo-swap-${uid} {
            0%   { opacity: 0; transform: scale(1.05); }
            100% { opacity: 1; transform: scale(1); }
          }
          @keyframes nisha-photo-breath-${uid} {
            0%, 100% { transform: scale(1) translateY(0); }
            50%      { transform: scale(1.02) translateY(-0.4px); }
          }
          @keyframes nisha-photo-talk-${uid} {
            0%, 100% { transform: scale(1) translateY(0); }
            50%      { transform: scale(1.03) translateY(-0.6px); }
          }
          @keyframes nisha-status-pulse-${uid} {
            0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(16,185,129,0.7); }
            50%      { opacity: 0.85; box-shadow: 0 0 0 4px rgba(16,185,129,0); }
          }
        `}</style>
      </span>

      {withStatus && (
        <span
          className="absolute bottom-0 right-0 block rounded-full ring-2 ring-white bg-emerald-500"
          style={{
            width: Math.max(8, size * 0.22),
            height: Math.max(8, size * 0.22),
            animation: `nisha-status-pulse-${uid} 2s ease-in-out infinite`,
          }}
          aria-label="Online"
        />
      )}

      {/* Bio popover */}
      {showBio && (
        <span
          role="tooltip"
          className={`pointer-events-none absolute z-50 rounded-xl bg-slate-900/95 text-white shadow-xl backdrop-blur ring-1 ring-white/10 whitespace-nowrap transition-all duration-200 ${popOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}`}
          style={{ ...popStyle, minWidth: 200 }}
          data-testid="nisha-bio-popover"
        >
          <span className="flex items-center gap-2 px-3 py-2.5">
            <span className="inline-block w-8 h-8 rounded-full overflow-hidden ring-1 ring-white/20 shrink-0">
              <img src={founder.src} alt="" className="w-full h-full object-cover" />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="text-[13px] font-black">{founder.name}</span>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-300">{founder.role}</span>
              <span className="text-[11px] text-slate-300 mt-1 whitespace-normal max-w-[220px] font-normal">{founder.greeting}</span>
            </span>
          </span>
        </span>
      )}
    </span>
  );
};

// -----------------------------------------------------------------------------
// SVG fallback (kept for any environment where /nisha-founder.jpg is missing)
// -----------------------------------------------------------------------------
const NishaSvgFallback = ({ uid, talking }) => (
  <span className="relative inline-flex items-center justify-center overflow-hidden w-full h-full">
    <svg
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height="100%"
      aria-hidden="true"
    >
        {/* All motion is CSS keyframes. Keep names uid-scoped so parallel
            avatars on the same page don't fight each other. */}
        <style>{`
          @keyframes nisha-breath-${uid} {
            0%, 100% { transform: scale(1) translateY(0); }
            50%      { transform: scale(1.015) translateY(-0.4px); }
          }
          @keyframes nisha-blink-${uid} {
            0%, 92%, 100% { transform: scaleY(1); }
            94%, 98%      { transform: scaleY(0.08); }
          }
          @keyframes nisha-hair-sway-${uid} {
            0%, 100% { transform: rotate(0deg); }
            50%      { transform: rotate(1.5deg); }
          }
          @keyframes nisha-blush-${uid} {
            0%, 100% { opacity: 0.55; }
            50%      { opacity: 0.85; }
          }
          @keyframes nisha-mouth-talk-${uid} {
            0%, 100% { transform: scaleY(1); }
            25%      { transform: scaleY(0.4); }
            50%      { transform: scaleY(1.6); }
            75%      { transform: scaleY(0.6); }
          }
          @keyframes nisha-earring-${uid} {
            0%, 100% { transform: translateY(0); }
            50%      { transform: translateY(0.6px); }
          }
          @keyframes nisha-status-pulse-${uid} {
            0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(16,185,129,0.7); }
            50%      { opacity: 0.85; box-shadow: 0 0 0 4px rgba(16,185,129,0); }
          }

          .nisha-root-${uid}      { transform-origin: 50% 60%; animation: nisha-breath-${uid} 4.4s ease-in-out infinite; }
          .nisha-eye-${uid}       { transform-origin: center; animation: nisha-blink-${uid} 5.2s ease-in-out infinite; transform-box: fill-box; }
          .nisha-eye-${uid}.right { animation-delay: 40ms; }
          .nisha-hair-left-${uid} { transform-origin: 30px 55px; transform-box: fill-box; animation: nisha-hair-sway-${uid} 6s ease-in-out infinite; }
          .nisha-hair-right-${uid}{ transform-origin: 70px 55px; transform-box: fill-box; animation: nisha-hair-sway-${uid} 6s ease-in-out infinite reverse; }
          .nisha-blush-${uid}     { animation: nisha-blush-${uid} 3.4s ease-in-out infinite; }
          .nisha-earring-${uid}   { animation: nisha-earring-${uid} 3s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
          .nisha-mouth-${uid}     { transform-origin: 50px 68px; transform-box: fill-box; ${talking ? `animation: nisha-mouth-talk-${uid} 0.55s ease-in-out infinite;` : ''} }
        `}</style>

        <defs>
          <radialGradient id={`nisha-bg-${uid}`} cx="50%" cy="35%" r="65%">
            <stop offset="0%" stopColor="#fef3c7" />
            <stop offset="100%" stopColor="#fbbf24" />
          </radialGradient>
          <linearGradient id={`nisha-blouse-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1f2937" />
            <stop offset="100%" stopColor="#000000" />
          </linearGradient>
        </defs>

        {/* soft warm background */}
        <rect width="100" height="100" rx="50" fill={`url(#nisha-bg-${uid})`} />

        {/* animation-root wraps everything for the idle breathing */}
        <g className={`nisha-root-${uid}`}>
          {/* back hair (full silhouette) */}
          <path
            d="M18 60 Q16 22 50 20 Q84 22 82 60 Q82 90 68 92 L68 62 Q68 55 62 54 L38 54 Q32 55 32 62 L32 92 Q18 90 18 60 Z"
            fill="#3b1f0f"
          />

          {/* animated side tresses */}
          <path
            className={`nisha-hair-left-${uid}`}
            d="M22 55 Q18 40 26 30 Q24 45 26 62 Z"
            fill="#2a1608"
          />
          <path
            className={`nisha-hair-right-${uid}`}
            d="M78 55 Q82 40 74 30 Q76 45 74 62 Z"
            fill="#2a1608"
          />

          {/* neck */}
          <rect x="43" y="66" width="14" height="12" rx="4" fill="#f2c9a3" />

          {/* face */}
          <ellipse cx="50" cy="52" rx="20" ry="22" fill="#f5cfa8" />

          {/* front hair bang */}
          <path
            d="M30 45 Q35 26 50 27 Q65 26 70 45 Q60 38 50 39 Q40 38 30 45 Z"
            fill="#3b1f0f"
          />
          {/* side sweep */}
          <path
            d="M28 50 Q26 34 40 30 Q35 40 33 55 Z"
            fill="#2a1608"
          />

          {/* eyebrows */}
          <path d="M38 48 Q42 46 46 48" stroke="#2a1608" strokeWidth="1.6" strokeLinecap="round" fill="none" />
          <path d="M54 48 Q58 46 62 48" stroke="#2a1608" strokeWidth="1.6" strokeLinecap="round" fill="none" />

          {/* eyes (blink animation on each) */}
          <ellipse className={`nisha-eye-${uid} left`}  cx="42" cy="54" rx="2.4" ry="3" fill="#1f2937" />
          <ellipse className={`nisha-eye-${uid} right`} cx="58" cy="54" rx="2.4" ry="3" fill="#1f2937" />
          {/* eye sparkles — sit above the pupils */}
          <circle cx="42.7" cy="53" r="0.8" fill="#fff" />
          <circle cx="58.7" cy="53" r="0.8" fill="#fff" />

          {/* blush (pulses gently) */}
          <ellipse className={`nisha-blush-${uid}`} cx="37" cy="60" rx="3" ry="1.8" fill="#f9a8a8" />
          <ellipse className={`nisha-blush-${uid}`} cx="63" cy="60" rx="3" ry="1.8" fill="#f9a8a8" />

          {/* nose */}
          <path d="M50 55 L48.5 61 Q50 62.5 51.5 61 Z" fill="#e2a577" />

          {/* mouth — animates when `talking` is true */}
          <g className={`nisha-mouth-${uid}`}>
            <path
              d="M43 66 Q50 71 57 66"
              stroke="#7c2d12"
              strokeWidth="1.8"
              fill="none"
              strokeLinecap="round"
            />
            <path d="M45 66.5 Q50 69 55 66.5" fill="#fff8f0" opacity="0.8" />
          </g>

          {/* earrings (subtle bob) */}
          <circle className={`nisha-earring-${uid}`} cx="30" cy="58" r="1.4" fill="#fde68a" stroke="#b45309" strokeWidth="0.4" />
          <circle className={`nisha-earring-${uid}`} cx="70" cy="58" r="1.4" fill="#fde68a" stroke="#b45309" strokeWidth="0.4" />

          {/* professional black Acre Mitra polo (matches founders' shirt) */}
          <path
            d="M22 100 Q22 80 40 76 L50 84 L60 76 Q78 80 78 100 Z"
            fill={`url(#nisha-blouse-${uid})`}
          />
          {/* V-neck opening */}
          <path d="M40 76 L50 84 L60 76 L50 90 Z" fill="#f5cfa8" />
          {/* subtle sheen */}
          <path d="M22 100 Q26 88 32 82 L34 100 Z" fill="#fff" opacity="0.06" />
          {/* Acre Mitra chest logo — small green mountain + yellow sun matching brand */}
          <g transform="translate(38 90)">
            {/* logo circle background */}
            <circle cx="0" cy="0" r="3.2" fill="#000000" stroke="#065f46" strokeWidth="0.4" />
            {/* mini green mountain silhouette */}
            <path d="M-2.2 1.4 L-0.6 -0.6 L0.4 0.6 L1.8 -1.2 L2.2 1.4 Z" fill="#22c55e" />
            {/* mini sun */}
            <circle cx="1.2" cy="-1.2" r="0.7" fill="#fbbf24" />
          </g>
        </g>
      </svg>
  </span>
);

export default NishaAvatar;
